import { useState, useMemo, useCallback } from 'react';

interface SliderConfig {
  id: string;
  label: string;
  min: number;
  max: number;
  step: number;
  defaultValue: number;
  format: (v: number) => string;
  icon: string;
}

const sliderConfigs: SliderConfig[] = [
  { id: 'sessionsPerDay', label: '일 평균 시술 건수', min: 1, max: 5, step: 1, defaultValue: 3, format: (v) => `${v}건`, icon: 'ri-scissors-cut-line' },
  { id: 'avgPrice', label: '시술 평균 단가', min: 10, max: 60, step: 5, defaultValue: 25, format: (v) => `${v}만 원`, icon: 'ri-price-tag-3-line' },
  { id: 'workDaysPerMonth', label: '월 영업일 수', min: 15, max: 26, step: 1, defaultValue: 22, format: (v) => `${v}일`, icon: 'ri-calendar-check-line' },
  { id: 'rent', label: '월 임대료', min: 50, max: 300, step: 10, defaultValue: 150, format: (v) => `${v}만 원`, icon: 'ri-building-2-line' },
  { id: 'consumableRate', label: '소모품 비율', min: 3, max: 10, step: 1, defaultValue: 5, format: (v) => `${v}%`, icon: 'ri-archive-line' },
  { id: 'marketingCost', label: '월 마케팅 비용', min: 0, max: 30, step: 5, defaultValue: 10, format: (v) => `${v}만 원`, icon: 'ri-megaphone-line' },
];

export default function RevenueCalculator() {
  const [values, setValues] = useState<Record<string, number>>(
    () => Object.fromEntries(sliderConfigs.map((c) => [c.id, c.defaultValue]))
  );

  const handleChange = useCallback((id: string, val: number) => {
    setValues((prev) => ({ ...prev, [id]: val }));
  }, []);

  const calc = useMemo(() => {
    const { sessionsPerDay, avgPrice, workDaysPerMonth, rent, consumableRate, marketingCost } = values;
    const monthlyRevenue = sessionsPerDay * avgPrice * workDaysPerMonth;
    const consumables = Math.round(monthlyRevenue * (consumableRate / 100));
    const royalty = 50;
    const otherCost = 38;
    const totalExpense = rent + consumables + marketingCost + royalty + otherCost;
    const monthlyProfit = monthlyRevenue - totalExpense;
    const profitRate = monthlyRevenue > 0 ? Math.max(0, (monthlyProfit / monthlyRevenue) * 100) : 0;
    const annualProfit = monthlyProfit * 12;
    return { monthlyRevenue, totalExpense, monthlyProfit, profitRate, annualProfit, consumables, royalty, otherCost };
  }, [values]);

  const isProfit = calc.monthlyProfit >= 0;

  return (
    <section style={{ backgroundColor: '#F7F5F2', padding: '96px 0' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 32px' }}>

        {/* 헤더 */}
        <div style={{ textAlign: 'center', marginBottom: '64px' }}>
          <span style={{ display: 'inline-block', fontSize: '11px', letterSpacing: '0.3em', textTransform: 'uppercase', color: '#C9A96E', marginBottom: '16px' }}>
            Revenue Simulator
          </span>
          <h2 className="text-[26px] md:text-[38px] break-keep" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 400, color: '#1A1A1A', lineHeight: 1.25, margin: '0 0 16px' }}>
            예상 수익 시뮬레이터
          </h2>
          <p style={{ fontSize: '14px', color: '#888', lineHeight: 1.7, margin: 0 }}>
            슬라이더를 조정해 나만의 수익 시나리오를 확인하세요
          </p>
        </div>

        {/* 순이익 하이라이트 배너 */}
        <div style={{
          background: 'linear-gradient(135deg, #1A1A1A 0%, #2D2D2D 100%)',
          borderRadius: '16px',
          padding: '32px 24px',
          marginBottom: '32px',
          borderLeft: '4px solid #C9A96E',
        }} className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div>
            <div style={{ fontSize: '12px', letterSpacing: '0.2em', textTransform: 'uppercase', color: '#C9A96E', marginBottom: '10px', fontWeight: 500 }}>
              월 예상 순이익
            </div>
            <div
              className="text-[40px] md:text-[52px] break-keep"
              style={{
                fontFamily: "'Cormorant Garamond', serif",
                fontWeight: 700,
                color: isProfit ? '#C9A96E' : '#FF6B6B',
                lineHeight: 1,
              }}
            >
              {calc.monthlyProfit.toLocaleString('ko-KR')}
              <span className="text-lg md:text-[22px] ml-2 font-normal" style={{ color: isProfit ? '#C9A96E' : '#FF6B6B' }}>만 원</span>
            </div>
          </div>
          <div className="grid grid-cols-2 md:flex md:flex-row gap-6 md:gap-12">
            {[
              { label: '월 매출', value: `${calc.monthlyRevenue.toLocaleString('ko-KR')}`, unit: '만 원', color: '#FFFFFF' },
              { label: '월 지출', value: `${calc.totalExpense.toLocaleString('ko-KR')}`, unit: '만 원', color: '#BBBBBB' },
              { label: '순이익률', value: `${calc.profitRate.toFixed(1)}`, unit: '%', color: isProfit ? '#C9A96E' : '#FF6B6B' },
              { label: '연간 순이익', value: `${calc.annualProfit.toLocaleString('ko-KR')}`, unit: '만 원', color: '#FFFFFF' },
            ].map((item) => (
              <div key={item.label} className="text-center">
                <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.55)', letterSpacing: '0.15em', textTransform: 'uppercase', marginBottom: '8px' }}>{item.label}</div>
                <div className="text-lg md:text-2xl font-semibold break-keep" style={{ fontFamily: "'Cormorant Garamond', serif", color: item.color }}>
                  {item.value}<span className="text-xs md:text-sm ml-1" style={{ color: item.color }}>{item.unit}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 슬라이더 + 지출 분석 */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }} className="calc-grid">
          {/* 슬라이더 */}
          <div style={{ backgroundColor: '#FFFFFF', borderRadius: '16px', padding: '40px', border: '1px solid #E8E4DF' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '32px', paddingBottom: '20px', borderBottom: '1px solid #F0EDE9' }}>
              <i className="ri-equalizer-2-line" style={{ color: '#C9A96E', fontSize: '18px' }} />
              <p style={{ fontSize: '14px', fontWeight: 600, color: '#1A1A1A', margin: 0 }}>매출 변수 설정</p>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '28px' }}>
              {sliderConfigs.map((config) => {
                const val = values[config.id];
                const pct = ((val - config.min) / (config.max - config.min)) * 100;
                return (
                  <div key={config.id}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <i className={config.icon} style={{ color: '#C9A96E', fontSize: '14px' }} />
                        <span style={{ fontSize: '13px', color: '#444', fontWeight: 500 }}>{config.label}</span>
                      </div>
                      <span style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '16px', fontWeight: 700, color: '#1A1A1A' }}>
                        {config.format(val)}
                      </span>
                    </div>
                    <div style={{ position: 'relative', height: '6px', backgroundColor: '#F0EDE9', borderRadius: '3px' }}>
                      <div style={{ position: 'absolute', left: 0, top: 0, height: '100%', backgroundColor: '#C9A96E', borderRadius: '3px', width: `${pct}%` }} />
                      <input
                        type="range"
                        min={config.min}
                        max={config.max}
                        step={config.step}
                        value={val}
                        onChange={(e) => handleChange(config.id, Number(e.target.value))}
                        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer', margin: 0 }}
                      />
                      <div style={{
                        position: 'absolute',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        left: `calc(${pct}% - 9px)`,
                        width: '18px',
                        height: '18px',
                        borderRadius: '50%',
                        backgroundColor: '#FFFFFF',
                        border: '2px solid #C9A96E',
                        pointerEvents: 'none',
                        boxShadow: '0 1px 4px rgba(0,0,0,0.12)',
                      }} />
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '6px' }}>
                      <span style={{ fontSize: '10px', color: '#CCCCCC' }}>{config.format(config.min)}</span>
                      <span style={{ fontSize: '10px', color: '#CCCCCC' }}>{config.format(config.max)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
            <div style={{ marginTop: '28px', paddingTop: '20px', borderTop: '1px solid #F0EDE9', display: 'flex', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setValues(Object.fromEntries(sliderConfigs.map((c) => [c.id, c.defaultValue])))}
                style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#AAAAAA', background: 'none', border: 'none', cursor: 'pointer' }}
              >
                <i className="ri-refresh-line" />
                기본값으로 초기화
              </button>
            </div>
          </div>

          {/* 지출 분석 + ROI */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            {/* 지출 내역 */}
            <div style={{ backgroundColor: '#FFFFFF', borderRadius: '16px', padding: '36px', border: '1px solid #E8E4DF', flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '28px', paddingBottom: '20px', borderBottom: '1px solid #F0EDE9' }}>
                <i className="ri-pie-chart-2-line" style={{ color: '#C9A96E', fontSize: '18px' }} />
                <p style={{ fontSize: '14px', fontWeight: 600, color: '#1A1A1A', margin: 0 }}>지출 내역 분석</p>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                {[
                  { label: '임대료', value: values.rent },
                  { label: '소모품비', value: calc.consumables },
                  { label: '마케팅비', value: values.marketingCost },
                  { label: '로열티', value: calc.royalty },
                  { label: '기타 운영비', value: calc.otherCost },
                ].map((item) => {
                  const pct = calc.totalExpense > 0 ? (item.value / calc.totalExpense) * 100 : 0;
                  return (
                    <div key={item.label}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                        <span style={{ fontSize: '12px', color: '#666' }}>{item.label}</span>
                        <span style={{ fontSize: '12px', fontWeight: 600, color: '#1A1A1A' }}>{item.value.toLocaleString('ko-KR')}만 원</span>
                      </div>
                      <div style={{ height: '5px', backgroundColor: '#F0EDE9', borderRadius: '3px', overflow: 'hidden' }}>
                        <div style={{ height: '100%', backgroundColor: '#C9A96E', borderRadius: '3px', width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
                <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '14px', borderTop: '1px solid #F0EDE9', marginTop: '4px' }}>
                  <span style={{ fontSize: '13px', fontWeight: 600, color: '#1A1A1A' }}>총 지출</span>
                  <span style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '16px', fontWeight: 700, color: '#1A1A1A' }}>
                    {calc.totalExpense.toLocaleString('ko-KR')}만 원
                  </span>
                </div>
              </div>
            </div>

            {/* 투자 회수 기간 */}
            <div style={{ backgroundColor: '#FFFFFF', borderRadius: '16px', padding: '36px', border: '1px solid #E8E4DF' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '24px', paddingBottom: '20px', borderBottom: '1px solid #F0EDE9' }}>
                <i className="ri-time-line" style={{ color: '#C9A96E', fontSize: '18px' }} />
                <p style={{ fontSize: '14px', fontWeight: 600, color: '#1A1A1A', margin: 0 }}>투자 회수 기간</p>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {[
                  { plan: '실속형', cost: 3600 },
                  { plan: '프리미엄형', cost: 4800 },
                  { plan: '자율시공형', cost: 2300 },
                ].map((item) => {
                  const months = calc.monthlyProfit > 0 ? Math.ceil(item.cost / calc.monthlyProfit) : 999;
                  const pct = Math.min(months <= 24 ? ((24 - months) / 24) * 100 : 0, 100);
                  return (
                    <div key={item.plan}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                        <span style={{ fontSize: '12px', color: '#666' }}>{item.plan} ({item.cost.toLocaleString()}만 원)</span>
                        <span style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '14px', fontWeight: 700, color: '#1A1A1A' }}>
                          {months > 24 ? '24개월+' : `${months}개월`}
                        </span>
                      </div>
                      <div style={{ height: '6px', backgroundColor: '#F0EDE9', borderRadius: '3px', overflow: 'hidden' }}>
                        <div style={{ height: '100%', backgroundColor: '#C9A96E', borderRadius: '3px', width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* 안내 문구 */}
        <p style={{ textAlign: 'center', fontSize: '11px', color: '#BBBBBB', marginTop: '40px', lineHeight: 1.6 }}>
          ※ 본 계산기는 예시 시뮬레이션이며, 실제 수익은 상권·운영 역량·시장 상황에 따라 달라질 수 있습니다.
        </p>
      </div>

      <style>{`
        @media (max-width: 1024px) {
          .calc-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </section>
  );
}